import React from 'react';
import styled from 'styled-components';
import { Dropdown, Icon } from '~/components';
import { useSession } from '~/contexts';
import { colors, weights } from '~/styles';

const LOCAL_STORAGE_KEY = 'ruddr_admin_ai_model';
const VALID_MODELS = ['gpt-5.4-mini', 'gpt-5.4'];

const S = {
  Dropdown: styled(Dropdown)`
    .dropdown-menu > div {
      border-radius: 0.5rem;
      box-shadow: 0 0 0.875rem rgba(0, 0, 0, 0.25);
      padding: 0.5rem 0;
      margin-top: 0.25rem;
    }
  `,

  Button: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.125rem;
    height: 2.125rem;
    min-width: 2.125rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${({ $active }) => ($active ? colors.primary : colors.grey75)};
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background: ${({ $active }) => ($active ? 'transparent' : colors.grey5)};
      color: ${({ $active }) => ($active ? colors.primary : 'inherit')};
    }
  `,

  Item: styled(Dropdown.Item)`
    font-size: 0.75rem;
    font-weight: ${({ $active }) => ($active ? weights.bold : weights.normal)};
    color: ${({ $active }) => ($active ? colors.primary : colors.black)};
  `,
};

export function useModelSelector() {
  const { isAdmin } = useSession();

  const [model, setModelState] = React.useState(() => {
    if (!isAdmin) return;
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (VALID_MODELS.includes(stored)) return stored;
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  });

  const setModel = (value) => {
    setModelState(value);
    if (value) {
      localStorage.setItem(LOCAL_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  };

  return [model, setModel];
}

export default function ModelSelector({ model, onModelChange }) {
  const { isAdmin } = useSession();

  if (!isAdmin) return null;

  return (
    <S.Dropdown align="right">
      {({ setIsOpen, isOpen }) => (
        <>
          <Dropdown.Trigger>
            <S.Button
              $active={isOpen || !!model}
              onClick={() => setIsOpen(!isOpen)}
              title={model ? `Model: ${model}` : 'Model: Default'}>
              <Icon icon="flask" />
            </S.Button>
          </Dropdown.Trigger>
          <Dropdown.Menu>
            <S.Item
              $active={!model}
              onClick={() => {
                onModelChange(undefined);
                setIsOpen(false);
              }}>
              Default
            </S.Item>
            {VALID_MODELS.map((m) => (
              <S.Item
                key={m}
                $active={model === m}
                onClick={() => {
                  onModelChange(m);
                  setIsOpen(false);
                }}>
                {m}
              </S.Item>
            ))}
          </Dropdown.Menu>
        </>
      )}
    </S.Dropdown>
  );
}
